import { logger } from 'core/utils/logger';
import { AvailabilityMethod } from 'generated-graphql/graphql';
import {
  apiCreateCalendarMeetingTemplateSchema,
  APICalendarMeetingTemplateSchema
} from '../api/useCreateCalendarMeetingTemplate';
import {
  CalendarMeetingTemplateFormData,
  MeetingLocation
} from '../constants/calendarMeetingTemplateSchema';
import { UseUserCalendarReturnData } from '../hooks/useUserCalendar';
import { convertTimeUnitValuesToMinutes } from './convertTimeUnitValuesToMinutes';
import { convertOwnerToParticipants } from './convertOwnerToParticipants';
import { convertFromParticipantsToParticipants } from './covertFromParticipantsToParticipants';
import { convertAvailabilityToSchema } from './convertAvailabilityToSchema';
import { getParticipantStats } from './getParticipantStats';

type MapOptions = {
  methodOverride?: AvailabilityMethod;
  // Edit seeds required-hours for display, so sending it always would write a
  // value onto a template that never had one.
  omitOutreachSettings?: boolean;
};

export const mapCreateCalendarMeetingTemplateSchema = async (
  data: CalendarMeetingTemplateFormData,
  userCalendars: UseUserCalendarReturnData,
  { methodOverride, omitOutreachSettings = false }: MapOptions = {}
): Promise<APICalendarMeetingTemplateSchema> => {
  const durationMinutes = convertTimeUnitValuesToMinutes(
    data.durationValue,
    data.durationUnit
  );

  const availabilityMethod = methodOverride ?? data.schedulingMethod;
  const isRoundRobin = availabilityMethod !== AvailabilityMethod.Collective;

  const owner = convertOwnerToParticipants(data.owner ?? '', userCalendars);
  const participants = convertFromParticipantsToParticipants(
    data.participants,
    userCalendars
  );

  // Round robin has no fixed organizer — the whole panel is the interviewer pool.
  const allParticipants = isRoundRobin
    ? [...owner, ...participants].map((participant) => ({
        ...participant,
        isOrganizer: false
      }))
    : [...owner, ...participants];

  const sendMeetingReminderMinutes = convertTimeUnitValuesToMinutes(
    data.sendMeetingReminderValue,
    data.sendMeetingReminderUnit
  );

  const bufferMinutes = convertTimeUnitValuesToMinutes(
    data.bufferTimeValue,
    data.bufferTimeUnit
  );

  const noticePeriodMinutes = convertTimeUnitValuesToMinutes(
    data.noticePeriodValue,
    data.noticePeriodUnit
  );

  const mappedData: APICalendarMeetingTemplateSchema = {
    title: data.title.trim(),
    description: data.description,
    videoConference: data.location === MeetingLocation.Video,
    durationMinutes,
    participants: allParticipants,
    availability: convertAvailabilityToSchema([
      // NOTE: 0 is Sunday in tech world apparently 😂
      { day: 0, availability: data.sundayAvailability },
      { day: 1, availability: data.mondayAvailability },
      { day: 2, availability: data.tuesdayAvailability },
      { day: 3, availability: data.wednesdayAvailability },
      { day: 4, availability: data.thursdayAvailability },
      { day: 5, availability: data.fridayAvailability },
      { day: 6, availability: data.saturdayAvailability }
    ]),
    timezone: data.timeZone,
    sendMeetingReminder: sendMeetingReminderMinutes,
    buffer: bufferMinutes,
    noticePeriodMinutes: noticePeriodMinutes,
    availableDaysInTheFuture: data.bookBeforeValue,
    // calendar-service will refuse a location with video conferencing.
    location:
      data.location === MeetingLocation.InPerson
        ? data.locationDetails?.trim()
        : undefined
  };

  // The field only shows for a virtual interviewer, but keeps its last value
  // while hidden.
  const { hasVirtualParticipant } = getParticipantStats(
    data.owner,
    data.participants,
    userCalendars
  );

  // An absent block means "leave the stored settings alone", so omitting keeps configured hours.
  if (
    !omitOutreachSettings &&
    hasVirtualParticipant &&
    typeof data.requiredHours === 'number'
  ) {
    mappedData.availabilityOutreachSettings = {
      requiredHours: data.requiredHours
    };
  }

  if (isRoundRobin) {
    mappedData.availabilityMethod = availabilityMethod;
  }

  try {
    // Validate the mapped data against the API schema
    await apiCreateCalendarMeetingTemplateSchema.validate(mappedData, {
      abortEarly: false
    });

    return mappedData;
  } catch (error) {
    if (error instanceof Error) {
      logger.error('Schema validation failed: ', error);
      throw new Error(`Schema validation failed: ${error.message}`);
    }
    throw error;
  }
};
